import { AvailablePlansTypes, PlanRecordKeyTypes } from '@constants/plans'
import {
  arePlansEqualPrice,
  isPlanCheaper,
  isPlanMoreExpensive
} from '@helpers/plans'
import { contactSupport } from '@helpers/support'
import { selectPlanAndGoToStep } from '@pages/Checkout/helpers/selectPlanAndGoToStep'
import { useCheckoutContext } from '@pages/Checkout/routes/contexts'
import { Button } from '@ui-kit'
import { ButtonVariant } from '@ui-kit/Button/types'
import { useTranslation } from 'react-i18next'

export const DOWNGRADE = 'DOWNGRADE'
export const UPGRADE = 'UPGRADE'
export const CONTACT_US = 'CONTACT_US'
export const CURRENT = 'CURRENT'

const CTAButton = ({
  recordKey,
  children,
  variant,
  onClick
}: {
  recordKey: PlanRecordKeyTypes
  children: string
  variant?: ButtonVariant
  onClick: (recordKey: PlanRecordKeyTypes) => void
}) => {
  const { t } = useTranslation()

  return (
    <Button
      variant={variant}
      size={16}
      onClick={() => onClick(recordKey)}
      fullWidth
    >
      {t(children)}
    </Button>
  )
}

const handleContactSupport = () => contactSupport()

const ContactUsButton = () => {
  const { t } = useTranslation()

  return (
    <Button variant='outlined' size={16} onClick={handleContactSupport}>
      {t('button.contact.us')}
    </Button>
  )
}

interface PlanCTAsTypes {
  [key: string]: {
    variant?: ButtonVariant
    text: string
  }
}

export const planCTAs: PlanCTAsTypes = {
  [DOWNGRADE]: {
    variant: 'outlined',
    text: 'change.plan.downgrade'
  },
  [UPGRADE]: {
    text: 'change.plan.page.upgrade'
  },
  [CURRENT]: {
    variant: 'outlined',
    text: 'change.plan.page.stay.with.this.plan'
  }
}

export type PlanCTAsType = typeof planCTAs

export const getPlanCTAName = ({
  recordKey,
  accountPlanRecordKey,
  action,
  isOldPlan
}: {
  recordKey: PlanRecordKeyTypes
  accountPlanRecordKey?: string
  action: () => void
  isOldPlan?: boolean
}) => {
  if (
    arePlansEqualPrice({
      plan1: recordKey,
      plan2: accountPlanRecordKey,
      isOldPlan
    })
  ) {
    return (
      <CTAButton
        recordKey={recordKey}
        variant={planCTAs[CURRENT].variant}
        onClick={action}
      >
        {planCTAs[CURRENT].text}
      </CTAButton>
    )
  }

  if (
    isPlanCheaper({
      plan1: recordKey,
      plan2: accountPlanRecordKey,
      isOldPlan
    })
  ) {
    return (
      <CTAButton
        recordKey={recordKey}
        variant={planCTAs[DOWNGRADE].variant}
        onClick={action}
      >
        {planCTAs[DOWNGRADE].text}
      </CTAButton>
    )
  }

  if (
    isPlanMoreExpensive({
      plan1: recordKey,
      plan2: accountPlanRecordKey,
      isOldPlan
    })
  ) {
    return (
      <CTAButton
        recordKey={recordKey}
        variant={planCTAs[UPGRADE].variant}
        onClick={action}
      >
        {planCTAs[UPGRADE].text}
      </CTAButton>
    )
  }

  return <ContactUsButton />
}

export const useGetPlanCTAButton = ({
  recordKey,
  isOldPlan
}: {
  recordKey: AvailablePlansTypes
  isOldPlan?: boolean
}) => {
  const { accountPlanRecordKey, goToStep, setCheckoutDataInContext } =
    useCheckoutContext()

  const handleSelectPlanAndGoToStep = () => {
    selectPlanAndGoToStep({
      recordKey,
      goToStep
    })

    setCheckoutDataInContext(prev => ({
      ...prev,
      chosenRecordKey: recordKey
    }))
  }

  return getPlanCTAName({
    recordKey,
    accountPlanRecordKey,
    action: handleSelectPlanAndGoToStep,
    isOldPlan
  })
}
